import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { ChangeEvent, FC, useEffect, useRef } from 'react';

import { G } from '@mobily/ts-belt';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { AddPhoto } from 'common/components/icons/AddPhoto';
import { useAzureObjectPhotoUpload } from '../hooks/useAzureObjectPhotoUpload';

type Props = {
  onChange: Lambda<string, void>;
  /** Optional callback to report blob URLs separately (for local display only) */
  onOfflineUpload?: (blobUrl: string) => void;
  /** Entity type for offline sync (e.g., 'trunk', 'switch', 'odf') */
  entityType?: string;
  /** Entity ID for offline sync */
  entityId?: string;
  /** Property ID for offline sync */
  propertyId?: number;
};

export const ImagePicker: FC<Props> = ({ onChange, onOfflineUpload, entityType, entityId, propertyId }) => {
  const { mutate, data: uploadedPhotoUrl, isLoading, isSuccess, isOfflineQueued } = useAzureObjectPhotoUpload();

  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>();
  const lastReportedUrl = useRef<string | null>(null);

  const onChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0);
    if (G.isNotNullable(file)) {
      // Reset the reported URL when starting a new upload
      lastReportedUrl.current = null;
      mutate({ file, entityType, entityId, propertyId });
    }
  };

  useEffect(() => {
    // Only call onChange once per unique URL
    if (isSuccess && G.isNotNullable(uploadedPhotoUrl) && uploadedPhotoUrl !== lastReportedUrl.current) {
      lastReportedUrl.current = uploadedPhotoUrl;

      if (isOfflineQueued) {
        // This is a blob URL - report it separately if callback provided
        // Do NOT call onChange with blob URLs - they shouldn't be saved to backend
        if (onOfflineUpload) {
          onOfflineUpload(uploadedPhotoUrl);
        } else {
          // For backwards compatibility, still call onChange but components should filter blob URLs
          onChange(uploadedPhotoUrl);
        }
      } else {
        // This is a real Azure URL - safe to save
        onChange(uploadedPhotoUrl);
      }
    }
  }, [isSuccess, uploadedPhotoUrl, onChange, onOfflineUpload, isOfflineQueued]);

  return (
    <>
      <Box
        sx={{
          visibility: 'hidden',
          position: 'absolute',
          height: 0,
        }}
        ref={inputRef}
        component="input"
        accept="image/*"
        type="file"
        onChange={onChangeFile}
      />
      <Button
        data-cy="add-photo"
        sx={{
          cursor: 'pointer',
          borderRadius: 0.5,
          width: 92,
          height: 92,
          border: '1px solid #DBE1E2',
          backgroundColor: '#F4F6F6',
          textTransform: 'uppercase',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        {isLoading && <CircularProgress />}
        {!isLoading && (
          <>
            <AddPhoto />
            <Typography sx={{ lineHeight: 1, fontSize: 12, color: '#707475' }}>{t('premise.addPhoto')}</Typography>
          </>
        )}
      </Button>
    </>
  );
};
